import React from 'react';
import i18n from 'i18next';
import classNames from 'classnames';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BrandingWatermarkOutlined from '@mui/icons-material/BrandingWatermarkOutlined';
import ClosedCaption from '@mui/icons-material/ClosedCaption';
import Forward10 from '@mui/icons-material/Forward10';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import PauseCircle from '@mui/icons-material/PauseCircle';
import PlayCircle from '@mui/icons-material/PlayCircle';
import Replay10 from '@mui/icons-material/Replay10';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeMute from '@mui/icons-material/VolumeMute';
import VolumeOff from '@mui/icons-material/VolumeOff';
import VolumeUp from '@mui/icons-material/VolumeUp';

import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';

import styles from '../styles/video.module.scss';

const PLAYBACK_SPEED_ICON_MAP = {
  0.5: 'speed0.5x',
  1.0: 'speed1x',
  1.25: 'speed1.25x',
  1.5: 'speed1.5x',
  1.75: 'speed1.75x',
  2.0: 'speed2x',
};

const forceDimensions = (value = 24) => {
  const style = `${value}px !important`;

  return { width: style, height: style };
};

const getVideoPlayerVolumeIcon = (volumeVal) => {
  if (volumeVal === 0) return <VolumeOff sx={forceDimensions()} />;
  if (volumeVal < 0.3) return <VolumeMute sx={forceDimensions()} />;
  if (volumeVal < 0.6) return <VolumeDown sx={forceDimensions()} />;
  return <VolumeUp sx={forceDimensions()} />;
};

const VideoPlayerControls = ({ className, children }) => (
  <div className={classNames(styles.controls, className)}>{children}</div>
);

const Duration = ({ played, duration }) => (
  <Typo className={styles.duration} variant="b01" element="span">
    {new Date(played * 1000).toISOString().slice(11, 19)} /{' '}
    {new Date(duration * 1000).toISOString().slice(11, 19)}
  </Typo>
);

const PlaybackSpeed = ({ playbackRate, handleSetPlaybackRate }) => (
  <Button
    ariaLabel={i18n.t('playback-speed')}
    className={classNames(styles.controlButton, styles.control)}
    icon={PLAYBACK_SPEED_ICON_MAP[playbackRate]}
    variant="ghost"
    onClick={() => handleSetPlaybackRate()}
    size="xsmall"
  />
);

const Play = ({ playing, handlePlay }) => (
  <Button
    ariaLabel={i18n.t('play')}
    className={classNames(styles.controlButton, styles.control, styles.play)}
    icon={
      playing ? <PauseCircle sx={forceDimensions(48)} /> : <PlayCircle sx={forceDimensions(48)} />
    }
    variant="ghost"
    onClick={handlePlay}
  />
);

const SkipBackward = ({ handleSeekChange }) => (
  <Button
    ariaLabel={i18n.t('skip-backward')}
    className={classNames(styles.controlButton, styles.control)}
    icon={<Replay10 sx={forceDimensions(36)} />}
    variant="ghost"
    onClick={() => handleSeekChange(-10)}
    size="small"
  />
);

const SkipForward = ({ handleSeekChange }) => (
  <Button
    ariaLabel={i18n.t('skip-forward')}
    className={classNames(styles.controlButton, styles.control)}
    icon={<Forward10 sx={forceDimensions(36)} />}
    variant="ghost"
    onClick={() => handleSeekChange(10)}
    size="small"
  />
);

const Volume = ({ volume, handleVolumeChange }) => (
  <div className={styles.volumeContainer}>
    <Button
      ariaLabel={i18n.t('volume')}
      className={classNames(styles.controlButton, styles.control)}
      icon={getVideoPlayerVolumeIcon(volume)}
      variant="ghost"
      size="xsmall"
      onClick={() => handleVolumeChange(!volume ? 0.5 : 0)}
    />
    <input
      type="range"
      min={0}
      max={1}
      value={volume}
      step="any"
      onMouseMove={(event) => event.stopPropagation()}
      onMouseUp={(event) => event.stopPropagation()}
      onChange={(event) => handleVolumeChange(event.target.value)}
    />
  </div>
);

const Bookmark = ({ handleBookmarkClick, played }) => (
  <Button
    ariaLabel={i18n.t('bookmark')}
    className={classNames(styles.controlButton, styles.control)}
    icon={<BookmarkIcon sx={forceDimensions()} />}
    variant="ghost"
    size="xsmall"
    onClick={() => handleBookmarkClick({ timestamp: played })}
  />
);

const Captions = ({ handleCaptionsClick, captions }) => (
  <Button
    ariaLabel={i18n.t('captions')}
    className={classNames(styles.controlButton, styles.control)}
    icon={captions === 'showing' ? 'captionsOn' : <ClosedCaption sx={forceDimensions()} />}
    variant="ghost"
    size="xsmall"
    onClick={() => handleCaptionsClick()}
  />
);

const Fullscreen = ({ fullscreen, handleFullScreen }) => (
  <div>
    {!fullscreen ? (
      <Button
        ariaLabel={i18n.t('fullscreen')}
        className={classNames(styles.controlButton, styles.control)}
        icon={<FullscreenIcon sx={forceDimensions()} />}
        variant="ghost"
        size="xsmall"
        onClick={() => handleFullScreen('maximise')}
      />
    ) : (
      <Button
        ariaLabel={i18n.t('exit-fullscreen')}
        className={classNames(styles.controlButton, styles.control)}
        icon={<FullscreenExit sx={forceDimensions()} />}
        variant="ghost"
        size="xsmall"
        onClick={() => handleFullScreen('minimise')}
      />
    )}
  </div>
);

const Pip = ({ pip, handleTogglePIP }) => {
  if (!pip) {
    return (
      <Button
        ariaLabel={i18n.t('expand')}
        className={classNames(styles.controlButton, styles.control)}
        icon={<BrandingWatermarkOutlined sx={forceDimensions()} />}
        variant="ghost"
        size="xsmall"
        onClick={handleTogglePIP}
      />
    );
  }

  return (
    <Button
      ariaLabel={i18n.t('minimize-screen')}
      className={classNames(styles.controlButton, styles.control)}
      icon="screenFull"
      variant="ghost"
      size="xsmall"
      onClick={handleTogglePIP}
    />
  );
};

VideoPlayerControls.Duration = Duration;
VideoPlayerControls.PlaybackSpeed = PlaybackSpeed;
VideoPlayerControls.Play = Play;
VideoPlayerControls.SkipBackward = SkipBackward;
VideoPlayerControls.SkipForward = SkipForward;
VideoPlayerControls.Volume = Volume;
VideoPlayerControls.Bookmark = Bookmark;
VideoPlayerControls.Captions = Captions;
VideoPlayerControls.Fullscreen = Fullscreen;
VideoPlayerControls.Pip = Pip;

export default VideoPlayerControls;
